import React, { useMemo, useState } from 'react';
import {
  BookReviewCharacter,
  BookReviewPage,
  BookReviewSnapshot,
  ReviewActionState,
  ReviewCheck,
  ReviewCheckName,
  ReviewCheckStatus,
  ReviewHistoryTarget,
  ReviewLayer,
  ReviewRevision,
  ReviewSafeIssue
} from '../types/bookReview';
import BookObservabilityDashboard from './BookObservabilityDashboard';
import ReviewPagePreview from './ReviewPagePreview';
import RevisionCompareDialog from './RevisionCompareDialog';
import './BookReviewDashboard.css';

type ReviewTab = 'pages' | 'characters';
type PageFilter = 'all' | 'attention' | 'ready';

export interface BookReviewDashboardProps {
  review: BookReviewSnapshot;
  canvasSize?: string;
  characterValidationAvailable?: boolean;
  busyAction?: ReviewActionState | null;
  resolveImageUrl?: (url: string) => string;
  onOpenPage?: (pageId: string) => void;
  onRegenerateLayer?: (pageId: string, layer: ReviewLayer) => void | Promise<void>;
  onSetLayerLock?: (pageId: string, layer: ReviewLayer, locked: boolean) => void | Promise<void>;
  onFixAllSafeIssues?: (issues: ReviewSafeIssue[]) => void | Promise<void>;
  onLoadHistory?: (target: ReviewHistoryTarget) => Promise<ReviewRevision[]>;
  onRestoreRevision?: (target: ReviewHistoryTarget, revisionId: string) => void | Promise<void>;
  onRefresh?: () => void | Promise<void>;
  onClose?: () => void;
}

interface HistoryDialogState {
  target: ReviewHistoryTarget;
  currentPage?: BookReviewPage;
  currentCharacter?: BookReviewCharacter;
  revisions: ReviewRevision[];
  selectedRevisionId?: string;
  loading: boolean;
  restoring: boolean;
  error: string | null;
}

const CHECK_LABELS: Record<ReviewCheckName, string> = {
  character: 'Character',
  text: 'Text',
  layout: 'Layout',
  dpi: 'DPI',
  job: 'Job'
};

const STATUS_LABELS: Record<ReviewCheckStatus, string> = {
  passed: 'Passed',
  warning: 'Warning',
  failed: 'Failed',
  unchecked: 'Unchecked',
  running: 'Running'
};

const STATUS_SYMBOLS: Record<ReviewCheckStatus, string> = {
  passed: '✓',
  warning: '△',
  failed: '!',
  unchecked: '?',
  running: '↻'
};

const LAYER_LABELS: Record<ReviewLayer, string> = {
  image: 'image',
  text: 'text',
  layout: 'layout'
};

const CHARACTER_UNCHECKED: ReviewCheck = {
  status: 'unchecked',
  label: 'Unchecked',
  message: 'Automated character identity validation is not available yet.'
};

const errorMessage = (error: unknown) => {
  if (error instanceof Error && error.message) return error.message;
  return 'That action could not be completed. Your saved work was not changed.';
};

const pageLabel = (entry: BookReviewPage) => (
  entry.pageTitle || entry.page.pageTitle || `Page ${entry.pageNumber}`
);

const effectiveCharacterCheck = (
  check: ReviewCheck,
  characterValidationAvailable: boolean
) => (characterValidationAvailable ? check : CHARACTER_UNCHECKED);

const pageHasAttention = (
  page: BookReviewPage,
  characterValidationAvailable: boolean
) => {
  const checks: ReviewCheck[] = [
    effectiveCharacterCheck(page.checks.character, characterValidationAvailable),
    page.checks.text,
    page.checks.layout,
    page.checks.dpi,
    page.checks.job
  ];
  return checks.some(check => check.status !== 'passed');
};

const ReviewStatusBadge: React.FC<{
  name: ReviewCheckName;
  check: ReviewCheck;
  testId?: string;
}> = ({ name, check, testId }) => {
  const statusLabel = STATUS_LABELS[check.status];
  const accessibleLabel = [
    `${CHECK_LABELS[name]}: ${statusLabel}`,
    check.issueCount ? `${check.issueCount} ${check.issueCount === 1 ? 'issue' : 'issues'}` : '',
    check.message || ''
  ].filter(Boolean).join('. ');

  return (
    <span
      className={`book-review-status-badge ${check.status}`}
      data-testid={testId || `review-badge-${name}`}
      data-check={name}
      data-status={check.status}
      aria-label={accessibleLabel}
      title={accessibleLabel}
    >
      <span aria-hidden="true">{STATUS_SYMBOLS[check.status]}</span>
      <span>{CHECK_LABELS[name]}</span>
      <strong>{statusLabel}</strong>
      {Boolean(check.issueCount) && <em>{check.issueCount}</em>}
    </span>
  );
};

const formatCheckedAt = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
};

const BookReviewDashboard: React.FC<BookReviewDashboardProps> = ({
  review,
  canvasSize = review.canvasSize || '6x9',
  characterValidationAvailable = false,
  busyAction,
  resolveImageUrl = value => value,
  onOpenPage,
  onRegenerateLayer,
  onSetLayerLock,
  onFixAllSafeIssues,
  onLoadHistory,
  onRestoreRevision,
  onRefresh,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState<ReviewTab>('pages');
  const [pageFilter, setPageFilter] = useState<PageFilter>('all');
  const [query, setQuery] = useState('');
  const [localAction, setLocalAction] = useState<ReviewActionState | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string>('');
  const [history, setHistory] = useState<HistoryDialogState | null>(null);
  const activeAction = busyAction || localAction;

  const safeIssues = useMemo(() => {
    const allIssues = review.safeIssues
      || review.pages.flatMap(page => page.safeIssues || []);
    return allIssues.filter(issue => issue.autoFixable);
  }, [review.pages, review.safeIssues]);
  const safeFixCount = Math.max(review.summary.safeFixCount, safeIssues.length);
  const hasUncheckedCharacterResults = review.summary.uncheckedCharacterPages > 0
    || review.pages.some(page => page.checks.character.status === 'unchecked')
    || review.characters.some(character => character.check.status === 'unchecked');

  const pageTotals = useMemo(() => {
    const attention = review.pages.filter(page => (
      pageHasAttention(page, characterValidationAvailable)
    )).length;
    return {
      attention,
      ready: Math.max(review.pages.length - attention, 0),
      unchecked: characterValidationAvailable
        ? review.summary.uncheckedCharacterPages
        : review.pages.length
    };
  }, [characterValidationAvailable, review.pages, review.summary.uncheckedCharacterPages]);

  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visiblePages = useMemo(() => review.pages.filter(page => {
    const needsAttention = pageHasAttention(page, characterValidationAvailable);
    const matchesFilter = pageFilter === 'all'
      || (pageFilter === 'attention' && needsAttention)
      || (pageFilter === 'ready' && !needsAttention);
    const matchesQuery = !normalizedQuery
      || pageLabel(page).toLocaleLowerCase().includes(normalizedQuery)
      || String(page.pageNumber).includes(normalizedQuery);
    return matchesFilter && matchesQuery;
  }), [characterValidationAvailable, normalizedQuery, pageFilter, review.pages]);

  const visibleCharacters = useMemo(() => review.characters.filter(character => (
    !normalizedQuery
      || character.name.toLocaleLowerCase().includes(normalizedQuery)
      || character.character.prompt.toLocaleLowerCase().includes(normalizedQuery)
  )), [normalizedQuery, review.characters]);

  const runAction = async (
    state: ReviewActionState,
    notice: string,
    action: () => void | Promise<void>
  ) => {
    setActionError(null);
    setActionNotice('');
    setLocalAction(state);
    try {
      await action();
      setActionNotice(notice);
    } catch (error) {
      setActionError(errorMessage(error));
    } finally {
      setLocalAction(null);
    }
  };

  const actionMatches = (type: ReviewActionState['type'], pageId?: string, layer?: ReviewLayer) => (
    activeAction?.type === type
      && (!pageId || activeAction.pageId === pageId)
      && (!layer || activeAction.layer === layer)
  );

  const openHistory = async (
    target: ReviewHistoryTarget,
    currentPage?: BookReviewPage,
    currentCharacter?: BookReviewCharacter
  ) => {
    const initial: HistoryDialogState = {
      target,
      currentPage,
      currentCharacter,
      revisions: [],
      loading: true,
      restoring: false,
      error: null
    };
    setHistory(initial);
    if (!onLoadHistory) {
      setHistory({ ...initial, loading: false, error: 'Version history is unavailable.' });
      return;
    }
    try {
      const revisions = await onLoadHistory(target);
      setHistory(previous => previous?.target.id === target.id
        ? {
            ...previous,
            revisions,
            selectedRevisionId: revisions[0]?.id,
            loading: false
          }
        : previous);
    } catch (error) {
      setHistory(previous => previous?.target.id === target.id
        ? { ...previous, loading: false, error: errorMessage(error) }
        : previous);
    }
  };

  const restoreRevision = async (revisionId: string) => {
    if (!history || !onRestoreRevision) return;
    const target = history.target;
    setHistory(previous => previous ? { ...previous, restoring: true, error: null } : previous);
    try {
      await onRestoreRevision(target, revisionId);
      setHistory(null);
      setActionNotice(`${target.label} was restored as a new revision.`);
    } catch (error) {
      setHistory(previous => previous
        ? { ...previous, restoring: false, error: errorMessage(error) }
        : previous);
    }
  };

  const checkedAt = formatCheckedAt(review.generatedAt);

  return (
    <section
      className="book-review-dashboard"
      data-testid="book-review-dashboard"
      data-book-revision={review.revision}
      aria-labelledby="book-review-title"
    >
      <header className="book-review-header">
        <div>
          <p className="book-review-eyebrow">Book-wide quality review</p>
          <h2 id="book-review-title">Review the whole book</h2>
          <p>
            Find problem pages, protect approved work, and repair only the layer that needs attention.
            Checked {checkedAt}.
          </p>
        </div>
        <div className="book-review-header-actions">
          {onRefresh && (
            <button
              type="button"
              className="book-review-button secondary"
              disabled={Boolean(activeAction)}
              onClick={() => runAction(
                { type: 'history' },
                'Review checks refreshed.',
                onRefresh
              )}
            >
              Refresh checks
            </button>
          )}
          <button
            type="button"
            className="book-review-button primary"
            data-testid="review-fix-all"
            disabled={!onFixAllSafeIssues || safeFixCount === 0 || Boolean(activeAction)}
            onClick={() => onFixAllSafeIssues && runAction(
              { type: 'fix-all' },
              `${safeFixCount} safe ${safeFixCount === 1 ? 'issue was' : 'issues were'} fixed.`,
              () => onFixAllSafeIssues(safeIssues)
            )}
          >
            {actionMatches('fix-all') ? 'Fixing safe issues…' : `Fix all safe issues (${safeFixCount})`}
          </button>
          {onClose && (
            <button
              type="button"
              className="book-review-icon-button"
              aria-label="Close book review"
              onClick={onClose}
            >
              ×
            </button>
          )}
        </div>
      </header>

      <dl className="book-review-summary" aria-label="Book review summary">
        <div>
          <dt>Total pages</dt>
          <dd data-testid="review-total-pages">{review.pages.length}</dd>
        </div>
        <div className="ready">
          <dt>Ready</dt>
          <dd data-testid="review-ready-pages">{pageTotals.ready}</dd>
        </div>
        <div className="attention">
          <dt>Need attention</dt>
          <dd data-testid="review-attention-pages">{pageTotals.attention}</dd>
        </div>
        <div className="unchecked">
          <dt>Character unchecked</dt>
          <dd data-testid="review-unchecked-pages">{pageTotals.unchecked}</dd>
        </div>
        <div>
          <dt>Safe fixes</dt>
          <dd data-testid="review-safe-fixes">{safeFixCount}</dd>
        </div>
      </dl>

      <BookObservabilityDashboard observability={review.observability} />

      {(!characterValidationAvailable || hasUncheckedCharacterResults) && (
        <div className="book-review-identity-notice" role="note" data-testid="character-validator-notice">
          <span aria-hidden="true">?</span>
          <div>
            <strong>Character identity is unchecked</strong>
            {characterValidationAvailable ? (
              <p>
                Identity validation is available, but these saved results have not been checked.
                They remain Unchecked—not Passed until a current comparison completes.
              </p>
            ) : (
              <p>
                No identity validator is connected, so character badges remain Unchecked—not Passed.
                Compare important scenes with the approved character references before export.
              </p>
            )}
          </div>
        </div>
      )}

      <div className="book-review-toolbar">
        <div className="book-review-tabs" role="tablist" aria-label="Review subject">
          <button
            type="button"
            id="book-review-pages-tab"
            role="tab"
            aria-controls="book-review-pages-panel"
            aria-selected={activeTab === 'pages'}
            className={activeTab === 'pages' ? 'active' : ''}
            onClick={() => setActiveTab('pages')}
          >
            Pages <span>{review.pages.length}</span>
          </button>
          <button
            type="button"
            id="book-review-characters-tab"
            role="tab"
            aria-controls="book-review-characters-panel"
            aria-selected={activeTab === 'characters'}
            className={activeTab === 'characters' ? 'active' : ''}
            onClick={() => setActiveTab('characters')}
          >
            Characters <span>{review.characters.length}</span>
          </button>
        </div>

        {activeTab === 'pages' && (
          <div className="book-review-filters" role="group" aria-label="Filter reviewed pages">
            <button
              type="button"
              aria-pressed={pageFilter === 'all'}
              className={pageFilter === 'all' ? 'active' : ''}
              onClick={() => setPageFilter('all')}
            >
              All {review.pages.length}
            </button>
            <button
              type="button"
              aria-pressed={pageFilter === 'attention'}
              className={pageFilter === 'attention' ? 'active' : ''}
              data-testid="review-filter-attention"
              onClick={() => setPageFilter('attention')}
            >
              Need attention {pageTotals.attention}
            </button>
            <button
              type="button"
              aria-pressed={pageFilter === 'ready'}
              className={pageFilter === 'ready' ? 'active' : ''}
              onClick={() => setPageFilter('ready')}
            >
              Ready {pageTotals.ready}
            </button>
          </div>
        )}

        <label className="book-review-search">
          <span>Search {activeTab}</span>
          <input
            type="search"
            value={query}
            placeholder={activeTab === 'pages' ? 'Page number or title' : 'Character name'}
            onChange={event => setQuery(event.target.value)}
          />
        </label>
      </div>

      <div className="book-review-announcements" aria-live="polite" aria-atomic="true">
        {actionNotice && <p className="success" data-testid="review-action-notice">{actionNotice}</p>}
        {actionError && <p className="error" role="alert">{actionError}</p>}
      </div>

      {activeTab === 'pages' && (
        <div
          id="book-review-pages-panel"
          role="tabpanel"
          aria-labelledby="book-review-pages-tab"
        >
          {visiblePages.length > 0 ? (
            <div className="book-review-page-grid" data-testid="review-page-grid">
              {visiblePages.map(entry => {
                const label = pageLabel(entry);
                const characterCheck = effectiveCharacterCheck(
                  entry.checks.character,
                  characterValidationAvailable
                );
                const needsAttention = pageHasAttention(entry, characterValidationAvailable);
                return (
                  <article
                    key={entry.pageId}
                    className={`book-review-page-card ${needsAttention ? 'attention' : 'ready'}`}
                    data-testid="review-page-card"
                    data-page-id={entry.pageId}
                    data-review-state={needsAttention ? 'attention' : 'ready'}
                  >
                    <header className="book-review-card-header">
                      <div>
                        <p>Page {entry.pageNumber}</p>
                        <h3>{label}</h3>
                      </div>
                      <span className={`book-review-card-state ${needsAttention ? 'attention' : 'ready'}`}>
                        {needsAttention ? 'Needs review' : 'Ready'}
                      </span>
                    </header>

                    <button
                      type="button"
                      className="book-review-preview-button"
                      aria-label={`Open ${label} in the editor`}
                      disabled={!onOpenPage}
                      onClick={() => onOpenPage?.(entry.pageId)}
                    >
                      <ReviewPagePreview
                        page={entry.page}
                        canvasSize={canvasSize}
                        ariaLabel={`${label} review thumbnail`}
                        testId={`review-page-preview-${entry.pageId}`}
                        resolveImageUrl={resolveImageUrl}
                      />
                    </button>

                    <div className="book-review-badge-grid" aria-label={`${label} quality checks`}>
                      <ReviewStatusBadge name="character" check={characterCheck} />
                      <ReviewStatusBadge name="text" check={entry.checks.text} />
                      <ReviewStatusBadge name="layout" check={entry.checks.layout} />
                      <ReviewStatusBadge name="dpi" check={entry.checks.dpi} />
                      <ReviewStatusBadge name="job" check={entry.checks.job} />
                    </div>

                    {entry.safeIssueCount > 0 && (
                      <p className="book-review-safe-issue-note">
                        {entry.safeIssueCount} safe {entry.safeIssueCount === 1 ? 'issue can' : 'issues can'} be fixed without regeneration.
                      </p>
                    )}

                    <fieldset className="book-review-locks">
                      <legend>Protect approved layers</legend>
                      {(['image', 'text', 'layout'] as ReviewLayer[]).map(layer => {
                        const pending = actionMatches('lock', entry.pageId, layer);
                        return (
                          <label key={layer}>
                            <input
                              type="checkbox"
                              checked={entry.lockedLayers[layer]}
                              disabled={!onSetLayerLock || Boolean(activeAction)}
                              data-testid={`review-lock-${layer}-${entry.pageId}`}
                              onChange={event => {
                                const locked = event.target.checked;
                                if (!onSetLayerLock) return;
                                runAction(
                                  { type: 'lock', pageId: entry.pageId, layer },
                                  `${label} ${LAYER_LABELS[layer]} layer ${locked ? 'locked' : 'unlocked'}.`,
                                  () => onSetLayerLock(entry.pageId, layer, locked)
                                );
                              }}
                            />
                            <span>{pending ? 'Saving…' : `Lock ${LAYER_LABELS[layer]}`}</span>
                          </label>
                        );
                      })}
                    </fieldset>

                    <div className="book-review-layer-actions" aria-label={`Regenerate a layer on ${label}`}>
                      {(['image', 'text', 'layout'] as ReviewLayer[]).map(layer => {
                        const pending = actionMatches('regenerate', entry.pageId, layer);
                        const locked = entry.lockedLayers[layer];
                        return (
                          <button
                            key={layer}
                            type="button"
                            data-testid={`review-regenerate-${layer}-${entry.pageId}`}
                            disabled={!onRegenerateLayer || locked || Boolean(activeAction)}
                            title={locked
                              ? `Unlock the ${LAYER_LABELS[layer]} layer before regenerating it.`
                              : `Regenerate only the ${LAYER_LABELS[layer]} layer`}
                            onClick={() => onRegenerateLayer && runAction(
                              { type: 'regenerate', pageId: entry.pageId, layer },
                              `${label} ${LAYER_LABELS[layer]} regeneration was queued.`,
                              () => onRegenerateLayer(entry.pageId, layer)
                            )}
                          >
                            {pending ? 'Queuing…' : `Regenerate ${LAYER_LABELS[layer]}`}
                          </button>
                        );
                      })}
                    </div>

                    <footer className="book-review-card-footer">
                      <button type="button" disabled={!onOpenPage} onClick={() => onOpenPage?.(entry.pageId)}>
                        Open page
                      </button>
                      <button
                        type="button"
                        data-testid={`review-history-${entry.pageId}`}
                        disabled={!onLoadHistory}
                        onClick={() => openHistory(
                          { type: 'page', id: entry.pageId, label },
                          entry
                        )}
                      >
                        History {entry.historyCount > 0 ? `(${entry.historyCount})` : ''}
                      </button>
                    </footer>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="book-review-empty" data-testid="review-empty-pages">
              <strong>No pages match this view.</strong>
              <p>Clear the search or choose another status filter.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'characters' && (
        <div
          id="book-review-characters-panel"
          role="tabpanel"
          aria-labelledby="book-review-characters-tab"
        >
          {visibleCharacters.length > 0 ? (
            <div className="book-review-character-grid" data-testid="review-character-grid">
              {visibleCharacters.map(entry => {
                const check = effectiveCharacterCheck(entry.check, characterValidationAvailable);
                const referenceUrl = entry.referenceImageUrl
                  || entry.character.thumbnailUrl
                  || entry.character.imageUrl;
                return (
                  <article
                    key={entry.characterId}
                    className="book-review-character-card"
                    data-testid="review-character-card"
                    data-character-id={entry.characterId}
                  >
                    <div className="book-review-character-image">
                      {referenceUrl ? (
                        <img src={resolveImageUrl(referenceUrl)} alt={`Approved reference for ${entry.name}`} />
                      ) : (
                        <span aria-hidden="true">?</span>
                      )}
                    </div>
                    <div className="book-review-character-copy">
                      <p>Approved reference</p>
                      <h3>{entry.name}</h3>
                      <ReviewStatusBadge
                        name="character"
                        check={check}
                        testId={`review-character-status-${entry.characterId}`}
                      />
                      <p className="book-review-character-prompt">
                        {entry.character.prompt || 'No character description saved.'}
                      </p>
                    </div>
                    <footer>
                      <button
                        type="button"
                        disabled={!onLoadHistory}
                        data-testid={`review-character-history-${entry.characterId}`}
                        onClick={() => openHistory(
                          { type: 'character', id: entry.characterId, label: entry.name },
                          undefined,
                          entry
                        )}
                      >
                        Compare history {entry.historyCount > 0 ? `(${entry.historyCount})` : ''}
                      </button>
                    </footer>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="book-review-empty">
              <strong>No characters match this search.</strong>
            </div>
          )}
        </div>
      )}

      {history && (
        <RevisionCompareDialog
          target={history.target}
          canvasSize={canvasSize}
          currentPage={history.currentPage?.page}
          currentCharacter={history.currentCharacter?.character}
          revisions={history.revisions}
          selectedRevisionId={history.selectedRevisionId}
          loading={history.loading}
          restoring={history.restoring}
          error={history.error}
          resolveImageUrl={resolveImageUrl}
          onSelectRevision={revisionId => setHistory(previous => previous
            ? { ...previous, selectedRevisionId: revisionId }
            : previous)}
          onRestore={restoreRevision}
          onClose={() => setHistory(null)}
        />
      )}
    </section>
  );
};

export default BookReviewDashboard;
